import qs from 'qs'
import storeFactory from 'common/modules/store'

import createNetworkMiddleWare, { _network } from '@piconetworks/network-middleware'

import { LOADER_GUEST, PURCHASE_ARTICLE_GUEST, OAUTH_USER_LOGIN } from '@piconetworks/endpoints'

import networkMiddlewareConfig from 'common/modules/middleware/networkMiddleware'
import metricsMiddleware from 'common/modules/middleware/metricsMiddleware'

import Loader from 'common/modules/loader'
import Arbiter from 'common/modules/arbiter'
import Lifecycle from 'common/modules/lifecycle'
import Metrics from 'common/modules/metrics'
import PopupQueue from 'common/modules/popupQueue'
import PopupTimer from 'common/modules/popupTimer'
import PopupsProcessor from 'common/modules/popupsProcessor'
import Me from 'common/modules/me'
import PaymentV2 from 'common/modules/paymentv2'
import Article from 'common/modules/article'
import Forms from 'common/modules/forms'
import Purchase from 'common/modules/purchase'

const autoTokenExceptions = [
    LOADER_GUEST(),
    PURCHASE_ARTICLE_GUEST(),
    OAUTH_USER_LOGIN(),
]

const generateStoreBundle = () => {
    const storeBundle = storeFactory({
        reducers: {
            _network,
            [Arbiter.store]: Arbiter.reducer,
            [PopupQueue.reducerName]: PopupQueue.reducer,
            [PopupTimer.reducerName]: PopupTimer.reducer,
        },
        initialStates: {
            [Arbiter.store]: Arbiter.initialState,
        },
        takes: [
            Arbiter.takes,
            Metrics.takes,
        ],
        middleware: [
            createNetworkMiddleWare({
                ...networkMiddlewareConfig,
                preRequestHook: async (config) => {
                    const { xprops = {} } = storeBundle.store.getState()?.zoid

                    const {
                        content,
                        adBlockDetector,
                        publisherId,
                        getSession,
                    } = xprops

                    const adBlocking = await adBlockDetector()

                    const headers = {
                        ...config.headers,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'from-domain': content.url || '', // migrate away from this
                        'current-page': content.url || '',
                        'prev-page': content.prev_page || document.referrer,
                        'session-id': await getSession() || '',
                        adBlocking,
                    }

                    // For some reason these are getting set down stream are causing the API
                    // to error out, I'm essentially resetting it here for the time being but
                    // the correct thing to do is to find the offending requests and fix them.
                    delete headers.publisherid
                    delete headers.publisherId

                    headers.publisherId = publisherId

                    return {
                        ...config,
                        headers,
                        paramsSerializer: (params) => qs.stringify(params),
                        timeout: 30000,
                    }
                },
                postRequestHook: async ({ config, response } = {}) => {
                    const { refresh_token: stateRefresh } = storeBundle.store.getState()?._network
                    const {
                        xprops: { getSession, setSession },
                    } = storeBundle.store.getState()?.zoid
                    const access_token = response?.data?.token
                    const refresh_token = response?.data?.refresh_token

                    if (autoTokenExceptions.includes(config.url) && stateRefresh?.default) {
                        return response
                    }

                    if ((access_token || refresh_token)) {
                        storeBundle.store.dispatch(Arbiter.creators.updateTokens({
                            accessToken: access_token,
                            refreshToken: refresh_token,
                        }))
                    }
                    const header = response?.headers?.['session-id']

                    // It's important to re-fetch this, because the xprops above only gets
                    // updated on a page refresh, and so the first-page visit ends up
                    // sending multiple requests with a blank session ID, rather than just the
                    // first one.
                    const session = await getSession()

                    setSession(header || session)

                    return response
                },
            }),
            metricsMiddleware({
                watchedActions: {
                    // [Lifecycle.types.LAUNCHER_PROMPT]: ({ payload: {
                    //     promptType,
                    //     triggerId
                    // } }) => ({
                    //     promptType,
                    //     triggerId,
                    // }),
                    [Lifecycle.types.POPUP_PROMPT]: ({
                        payload: {
                            promptType,
                            triggerId,
                        },
                    }) => ({
                        promptType,
                        triggerId,
                    }),
                    // [Lifecycle.types.DIRECT_LINK_PROMPT]: ({ payload: { triggerId } }) => ({
                    //     promptType: 'payment_popup',
                    //     triggerId,
                    // }),
                    // [Lifecycle.types.PLAN_LINK_PROMPT]: ({ payload: { triggerId } }) => ({
                    //     promptType: 'payment_popup',
                    //     triggerId,
                    // }),
                    // [Lifecycle.types.MENU_PROMPT]: () => ({
                    //     promptType: 'purchase',
                    //     triggerId: 'menu',
                    // }),
                },
                onPromptShown: ({ promptType, triggerId }) => {
                    const state = storeBundle.store.getState()
                    const articleId = Loader.selectors.articleId(state)

                    if (triggerId) {
                        storeBundle.store.dispatch(Metrics.creators.sendMessage({
                            payload: {
                                promptType,
                                triggerId,
                                articleId,
                            },
                        }))
                    }
                },
            }),
            PopupQueue.middleware,
            PopupTimer.middleware,
        ],
        modules: [
            Loader,
            Lifecycle,
            PopupsProcessor,
            Me,
            PaymentV2,
            Article,
            Forms,
            Purchase,
        ],
    })

    return storeBundle
}

export default generateStoreBundle
